import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
  selector: '[pdsTooltip]',
  standalone: false,
})
export class PDSTooltipDirective implements OnDestroy {
  @Input('pdsTooltip')
  tooltipText: string;
  @Input('pdsTooltipPosition')
  tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
  tooltipElement: HTMLElement | null;

  constructor(private el: ElementRef, private renderer: Renderer2) {
    this.tooltipText = '';
    this.tooltipElement = null;
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (!this.tooltipElement) {
      this.showTooltip();
    }
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (this.tooltipElement) {
      this.hideTooltip();
    }
  }

  ngOnDestroy() {
    this.hideTooltip();
  }

  private showTooltip() {
    const element = this.renderer.createElement('span');
    element.innerText = this.tooltipText;
    this.renderer.appendChild(document.body, element);

    this.renderer.setStyle(element, 'position', 'absolute');
    this.renderer.setStyle(element, 'background-color', '#333');
    this.renderer.setStyle(element, 'color', '#fff');
    this.renderer.setStyle(element, 'padding', '5px 10px');
    this.renderer.setStyle(element, 'border-radius', '4px');
    this.renderer.setStyle(element, 'font-size', '12px');
    this.renderer.setStyle(element, 'white-space', 'nowrap');
    this.renderer.setStyle(element, 'pointer-events', 'none');

    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.calculateTooltipPosition(element, hostPos);

    this.renderer.setStyle(element, 'top', `${tooltipPos.top}px`);
    this.renderer.setStyle(element, 'left', `${tooltipPos.left}px`);

    this.tooltipElement = element;
  }

  private hideTooltip() {
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }
  }

  private calculateTooltipPosition(tooltipElement: HTMLElement, hostPos: DOMRect): { top: number; left: number } {
    if (this.tooltipPosition === 'top') {
      const horizontalCenter = this.calculateHorizontalCenter(hostPos, tooltipElement);
      const leftPosition = horizontalCenter <= 0 ? 10 : horizontalCenter;
      return {
        top: hostPos.top + window.scrollY - tooltipElement.offsetHeight - 10,
        left: leftPosition,
      };
    } else if (this.tooltipPosition === 'bottom') {
      const horizontalCenter = this.calculateHorizontalCenter(hostPos, tooltipElement);
      const leftPosition = horizontalCenter <= 0 ? 10 : horizontalCenter;
      return {
        top: hostPos.bottom + window.scrollY + 10,
        left: leftPosition,
      };
    } else if (this.tooltipPosition === 'left') {
      return {
        top: this.calculateVerticalCenter(hostPos, tooltipElement),
        left: hostPos.left + window.scrollX - tooltipElement.offsetWidth - 10,
      };
    } else if (this.tooltipPosition === 'right') {
      return {
        top: this.calculateVerticalCenter(hostPos, tooltipElement),
        left: hostPos.right + window.scrollX + 10,
      };
    } else {
      return {
        top: hostPos.top + window.scrollY - tooltipElement.offsetHeight - 10,
        left: this.calculateHorizontalCenter(hostPos, tooltipElement),
      };
    }
  }

  private calculateHorizontalCenter(hostPos: DOMRect, tooltipElement: HTMLElement): number {
    return hostPos.left + window.scrollX + hostPos.width / 2 - tooltipElement.offsetWidth / 2;
  }

  private calculateVerticalCenter(hostPos: DOMRect, tooltipElement: HTMLElement): number {
    return hostPos.top + window.scrollY + hostPos.height / 2 - tooltipElement.offsetHeight / 2;
  }
}
